import axios from 'axios';
import appConfig from '../config/appConfig';
import { API } from '../config/apiEndpoints';
import tokenStore from './tokenStore';

// A consistent error for every failed API call.
export class ApiError extends Error {
  constructor({ message, status = 0, code = 'UNKNOWN', fieldErrors = {}, details, cancelled = false }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.details = details;
    this.cancelled = cancelled;
  }
}

const STATUS_MESSAGES = {
  400: 'Some details are invalid. Please check and try again.',
  401: 'Please sign in to continue.',
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: 'This was changed by someone else. Refresh and try again.',
  413: 'The file is too large.',
  422: 'Some details are invalid. Please check and try again.',
  429: 'Too many attempts. Please wait a moment and try again.',
};

// Accepts { errors: { field: msg } } or { errors: [{ field, message }] }.
const toFieldErrors = (errors) => {
  if (!errors) return {};
  if (Array.isArray(errors)) return Object.fromEntries(errors.filter((e) => e && e.field).map((e) => [e.field, e.message]));
  return typeof errors === 'object' ? errors : {};
};

export function toApiError(error) {
  if (error instanceof ApiError) return error;
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
    return new ApiError({ message: 'Request cancelled.', code: 'CANCELLED', cancelled: true });
  }
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return new ApiError({ message: 'The server took too long to respond. Please try again.', code: 'TIMEOUT' });
  }
  if (!error?.response) {
    return new ApiError({ message: "Can't reach the server. Check your connection and try again.", code: 'NETWORK' });
  }
  const { status, data } = error.response;
  // Only show server messages for client errors; 5xx details may leak internals.
  const serverMessage = status < 500 && typeof data?.message === 'string' ? data.message : null;
  return new ApiError({
    status,
    code: data?.code || `HTTP_${status}`,
    message: serverMessage || STATUS_MESSAGES[status] || 'Something went wrong. Please try again.',
    fieldErrors: toFieldErrors(data?.errors),
    details: status < 500 ? data : undefined,
  });
}

const apiOrigin = () => new URL(appConfig.api.baseURL, window.location.origin).origin;

// The access token is only ever sent to our own API, never to absolute third-party URLs.
const isOwnApi = (url = '') => {
  if (!/^([a-z][a-z\d+.-]*:)?\/\//i.test(url)) return true;
  try {
    return new URL(url, window.location.origin).origin === apiOrigin();
  } catch {
    return false;
  }
};

const shared = {
  baseURL: appConfig.api.baseURL,
  timeout: appConfig.api.timeoutMs,
  withCredentials: appConfig.api.withCredentials,
  xsrfCookieName: appConfig.api.xsrfCookieName,
  xsrfHeaderName: appConfig.api.xsrfHeaderName,
  headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
};

const apiClient = axios.create(shared);

// Separate instance for token refresh so it never loops through the 401 handler.
const refreshClient = axios.create(shared);

let refreshInFlight = null;

// Default refresh: exchange the httpOnly refresh cookie for a new access token.
async function defaultRefresh() {
  const { data } = await refreshClient.post(API.auth.refresh);
  tokenStore.set(data.accessToken, data.expiresIn);
  return data;
}

const handlers = { refresh: defaultRefresh, onUnauthorized: () => {} };

// AuthProvider plugs in its own refresh and sign-out behaviour.
export function configureApiAuth({ refresh, onUnauthorized } = {}) {
  handlers.refresh = refresh || defaultRefresh;
  handlers.onUnauthorized = onUnauthorized || (() => {});
}

// All concurrent 401s share one refresh call.
export function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve()
      .then(() => handlers.refresh())
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

apiClient.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token && isOwnApi(config.url) && !config.skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (typeof config.headers?.delete === 'function') {
    config.headers.delete('Authorization');
  } else if (config.headers) {
    delete config.headers.Authorization;
  }
  if (!isOwnApi(config.url)) config.withCredentials = false; // no cookies to other sites
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (status === 401 && !original._retried && !original.skipAuthRefresh && isOwnApi(original.url)) {
      original._retried = true;
      try {
        await refreshAccessToken();
        return apiClient(original);
      } catch {
        tokenStore.clear();
        handlers.onUnauthorized('expired');
      }
    }
    throw toApiError(error);
  }
);

export default apiClient;
